import { CircleHitbox, GroupHitbox, RectangleHitbox, type Hitbox } from "./hitbox";
import { Vec, type Vector } from "./vector";

export interface RoofOccluder {
    readonly hitbox?: Hitbox
    readonly ceilingHitbox?: Hitbox
    readonly ceilingVisible?: boolean
    readonly dead: boolean
    readonly damageable: boolean
    readonly isBuilding?: boolean
    readonly definition: {
        readonly isDoor?: boolean
        readonly isWall?: boolean
        readonly isWindow?: boolean
        readonly noCollisions?: boolean
        readonly hasDamagedCeiling?: boolean
    }
    readonly door?: { readonly closedHitbox?: RectangleHitbox, readonly offset: number }
}

/** Exclude the eaves and wall band from center-based entry detection. Explicit room bounds take precedence. */
export function roofInterior(roof: Hitbox): Hitbox {
    const inset = (box: CircleHitbox | RectangleHitbox): CircleHitbox | RectangleHitbox => {
        if (box instanceof CircleHitbox) return new CircleHitbox(Math.max(0.1, box.radius - 2), box.position);
        const margin = Math.min(2, (box.max.x - box.min.x) / 4, (box.max.y - box.min.y) / 4);
        return new RectangleHitbox(Vec(box.min.x + margin, box.min.y + margin), Vec(box.max.x - margin, box.max.y - margin));
    };
    if (!(roof instanceof GroupHitbox)) return roof instanceof RectangleHitbox || roof instanceof CircleHitbox ? inset(roof) : roof.clone();
    const regions = roof.hitboxes.map(inset);
    // Joined roof sections have an internal seam, not exterior eaves. Connect their
    // inset regions only where the original rectangles actually touch or overlap.
    for (let i = 0; i < roof.hitboxes.length; i++) for (let j = i + 1; j < roof.hitboxes.length; j++) {
        const a = roof.hitboxes[i], b = roof.hitboxes[j], ia = regions[i], ib = regions[j];
        if (!(a instanceof RectangleHitbox && b instanceof RectangleHitbox && ia instanceof RectangleHitbox && ib instanceof RectangleHitbox)) continue;
        const xMin = Math.max(ia.min.x, ib.min.x), xMax = Math.min(ia.max.x, ib.max.x);
        const yMin = Math.max(ia.min.y, ib.min.y), yMax = Math.min(ia.max.y, ib.max.y);
        if (xMin < xMax && Math.max(a.min.y, b.min.y) <= Math.min(a.max.y, b.max.y)) {
            const top = ia.min.y < ib.min.y ? ia : ib, bottom = top === ia ? ib : ia;
            if (top.max.y < bottom.min.y) regions.push(new RectangleHitbox(Vec(xMin, top.max.y - 0.001), Vec(xMax, bottom.min.y + 0.001)));
        }
        if (yMin < yMax && Math.max(a.min.x, b.min.x) <= Math.min(a.max.x, b.max.x)) {
            const left = ia.min.x < ib.min.x ? ia : ib, right = left === ia ? ib : ia;
            if (left.max.x < right.min.x) regions.push(new RectangleHitbox(Vec(left.max.x - 0.001, yMin), Vec(right.min.x + 0.001, yMax)));
        }
    }
    return new GroupHitbox(...regions);
}

export interface RoofCandidate<T extends RoofOccluder> { readonly object: T, readonly opening?: Hitbox }

export function hasOpaqueRoofAt<T extends RoofOccluder>(position: Vector, candidates: readonly RoofCandidate<T>[], sameLayer: (object: T) => boolean): boolean {
    return candidates.some(({ object }) => object.isBuilding && object.ceilingVisible && !object.dead
        && sameLayer(object) && object.ceilingHitbox?.isPointInside(position)) === true;
}

/** Membership changes rebuild this small spatial cache. Door movement/death is read live through object references. */
export class RoofOcclusionIndex<T extends RoofOccluder> {
    private readonly cells = new Map<string, RoofCandidate<T>[]>();
    private readonly seen = new Set<RoofCandidate<T>>();
    private readonly result: RoofCandidate<T>[] = [];

    rebuild(objects: Iterable<T>, opening: (object: T) => Hitbox | undefined): void {
        this.cells.clear();
        for (const object of objects) {
            if ((!object.damageable && !object.ceilingHitbox) || (!object.hitbox && !object.ceilingHitbox) || object.definition.noCollisions) continue;
            const portal = opening(object), region = portal ?? object.hitbox ?? object.ceilingHitbox;
            if (!region) continue;
            const bounds = region.toRectangle().clone();
            if (object.ceilingHitbox) {
                const roof = object.ceilingHitbox.toRectangle();
                bounds.min.x = Math.min(bounds.min.x, roof.min.x);
                bounds.min.y = Math.min(bounds.min.y, roof.min.y);
                bounds.max.x = Math.max(bounds.max.x, roof.max.x);
                bounds.max.y = Math.max(bounds.max.y, roof.max.y);
            }
            // Open doors can swing out of their closed footprint, but never out of this cached envelope.
            const pad = object.definition.isDoor ? 16 : 0;
            const entry = { object, opening: portal };
            for (let x = Math.floor((bounds.min.x - pad) / 32); x <= Math.floor((bounds.max.x + pad) / 32); x++) {
                for (let y = Math.floor((bounds.min.y - pad) / 32); y <= Math.floor((bounds.max.y + pad) / 32); y++) {
                    const key = `${x},${y}`;
                    const cell = this.cells.get(key);
                    if (cell) cell.push(entry); else this.cells.set(key, [entry]);
                }
            }
        }
    }

    query(position: Vector, radius: number): readonly RoofCandidate<T>[] {
        this.result.length = 0;
        this.seen.clear();
        for (let x = Math.floor((position.x - radius) / 32); x <= Math.floor((position.x + radius) / 32); x++) {
            for (let y = Math.floor((position.y - radius) / 32); y <= Math.floor((position.y + radius) / 32); y++) {
                for (const entry of this.cells.get(`${x},${y}`) ?? []) {
                    if (this.seen.has(entry)) continue;
                    this.seen.add(entry);
                    this.result.push(entry);
                }
            }
        }
        return this.result;
    }
}

/** Outside viewers need an actual open door or destroyed wall/window, and an unobstructed ray into the room. */
export function canRevealRoof<T extends RoofOccluder>(
    position: Vector, roof: Hitbox, interior: Hitbox, candidates: readonly RoofCandidate<T>[],
    sameLayer: (object: T) => boolean, visionSize: number
): boolean {
    if (interior.isPointInside(position)) return true;
    for (const { object, opening } of candidates) {
        if (!opening || !sameLayer(object) || !roof.collidesWith(new CircleHitbox(4, opening.getCenter()))) continue;
        if (!((object.definition.isDoor && (object.dead || (object.door?.offset ?? 0) !== 0))
            || ((object.definition.isWall || object.definition.isWindow) && object.dead))) continue;
        const bounds = opening.toRectangle(), horizontal = bounds.max.x - bounds.min.x > bounds.max.y - bounds.min.y;
        let clear = 0;
        for (const t of [0.25, 0.375, 0.5, 0.625, 0.75]) {
            const point = Vec(
                bounds.min.x + (bounds.max.x - bounds.min.x) * (horizontal ? t : 0.5),
                bounds.min.y + (bounds.max.y - bounds.min.y) * (horizontal ? 0.5 : t)
            );
            const dx = point.x - position.x, dy = point.y - position.y, distance = Math.hypot(dx, dy);
            if (distance < 0.01 || distance > visionSize) continue;
            // Continue past the roof edge and actual wall plane. Stopping at the eaves leaks closed rooms.
            const end = Vec(point.x + dx / distance * 6, point.y + dy / distance * 6);
            if (!interior.isPointInside(end)) continue;
            let blocked = false;
            for (const { object: blocker } of candidates) {
                if (!sameLayer(blocker) || (blocker.dead && !(blocker.isBuilding && blocker.definition.hasDamagedCeiling))) continue;
                if (blocker.hitbox?.intersectsLine(position, end)) { blocked = true; break; }
            }
            if (!blocked && ++clear >= 2) return true;
        }
    }
    return false;
}
